import { PLAN_FEATURES, PLAN_TEXT_FEATURES } from '@scio3/shared-types';

/**
 * Plan tier ordering for comparison.
 * Higher index = higher tier.
 */
export const PLAN_TIER_ORDER = [
  PLAN_FEATURES.FREE_PLAN,
  PLAN_FEATURES.STANDARD,
  PLAN_FEATURES.PRO,
  PLAN_FEATURES.PRO_PLUS,
  PLAN_FEATURES.ENGAGE,
  PLAN_FEATURES.ENTERPRISE,
] as const;

/**
 * Compare two plan tiers. Returns:
 *  - negative if a < b (a is lower tier)
 *  - 0 if equal
 *  - positive if a > b (a is higher tier)
 */
export function comparePlanTiers(a: string, b: string): number {
  const indexA = PLAN_TIER_ORDER.indexOf(a as (typeof PLAN_TIER_ORDER)[number]);
  const indexB = PLAN_TIER_ORDER.indexOf(b as (typeof PLAN_TIER_ORDER)[number]);
  return (indexA === -1 ? 0 : indexA) - (indexB === -1 ? 0 : indexB);
}

/**
 * Get human-readable plan name.
 */
export function getPlanDisplayName(planFeature: string): string {
  return PLAN_TEXT_FEATURES[planFeature] ?? planFeature;
}

/**
 * Calculate yearly savings percentage vs monthly.
 */
export function calcYearlySavingsPercent(monthlyPrice: number, yearlyPrice: number): number {
  if (monthlyPrice <= 0) return 0;
  const yearlyPerMonth = yearlyPrice / 12;
  return Math.ceil(((monthlyPrice - yearlyPerMonth) / monthlyPrice) * 100);
}

/**
 * Get currency symbol for display.
 * Matches scio1's getCurrencyValue().
 */
export function getCurrencySymbol(currency: string): string {
  switch (currency) {
    case 'usd':
      return '$';
    case 'aud':
      return 'A$';
    case 'cad':
      return 'CA$';
    case 'eur':
      return '\u20AC';
    case 'gbp':
      return '\u00A3';
    default:
      return '$';
  }
}

/**
 * Extract first node from planFeatures relay-style paginated response.
 * Eliminates repeated `Record<string, unknown>` casting across billing components.
 */
export function extractPlanFeaturesNode<T = Record<string, unknown>>(data: unknown): T | undefined {
  const raw = data as Record<string, unknown> | undefined;
  const page = (raw?.planFeatures as Record<string, unknown>)?.page as Record<string, unknown>;
  const edges = (page?.edges ?? []) as { node: T }[];
  return edges[0]?.node;
}

/**
 * Apply coupon discount to a total.
 * Handles both percent_off and amount_off (amount_off is in cents from Stripe).
 */
export function applyCouponDiscount(
  total: number,
  coupon?: { percent_off?: number; amount_off?: number },
): number {
  if (!coupon) return total;
  if (coupon.percent_off) return (total * (100 - coupon.percent_off)) / 100;
  if (coupon.amount_off) return total - coupon.amount_off / 100;
  return total;
}

/**
 * Safely extract a mutation response field from Apollo result.data.
 */
export function extractMutationResponse<T = Record<string, unknown>>(
  data: unknown,
  operationName: string,
): T | undefined {
  return (data as Record<string, T | undefined>)?.[operationName];
}

/**
 * Format currency amount for display.
 * Amounts from Stripe are in cents — divide by 100 first if needed.
 */
export function formatCurrencyAmount(
  amount: number,
  currency = 'usd',
  options?: { fromCents?: boolean },
): string {
  const value = options?.fromCents ? amount / 100 : amount;
  const currencyUpper = currency.toUpperCase();

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyUpper,
      minimumFractionDigits: 2,
    }).format(value);
  } catch {
    // Fallback for unknown currency codes
    return `${currencyUpper} ${value.toFixed(2)}`;
  }
}
